import React, { ChangeEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Avatar, Box, Button, Card, Chip, CircularProgress, Divider, Switch, Tooltip, Typography, } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { clientTheme } from "../../../theme/clientTheme";

const isModernTheme = clientTheme.layout?.portfolioCardVariant === "modern";
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from "@mui/icons-material/Warning";

import { SearchField, DrawerControl, StatusChip, Drawer, FilterSideBar } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { RiskDetails } from "./RiskDetails";
import { ScroreCard } from "./ScroreCard";
import { RaiseRisk } from "./RaiseRisk";

import { useRiskRegister } from "../mutation";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { RiskHeader } from "./RiskDetailsOverview";
import { useFilterContext } from "../../../shared/context";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useActiveFilterLabels } from "../../../shared/hooks";


interface RiskRegisterDataProps {
    "risk_id": string,
    "project_ref": string,
    "project_name": string,
    "project_id": number,
    "title": string,
    "risk_description": string,
    "review_date": string,
    "category_display_text": string,
    "cause_title": string,
    "status": string,
    "impact": string,
    "probability": string,
    "divident": number,
    "risk_ref": null,
    "score": number,
    "severity": string,
    "status_display_text": string,
    "color": string,
    "assessment_status": string,
    "impact_display": string,
    "probability_display": string
};

export const RiskRegister: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const { isAllowedToRaiseRisk } = useCapabilities();

    const [searchParams, setSearchParams] = useSearchParams();
    const { quickFilters, options: filterOptions } = useFilterContext();
    const { getFilterOptions } = useDisplayField([]);
    const { hasActiveFilters, activeFilterLabels } = useActiveFilterLabels();

    const selected = searchParams.get('selected');
    const id = searchParams.get('id');

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
    const handleOpenFilter = () => setIsOpenFilter(true);
    const handleCloseFilter = () => setIsOpenFilter(false);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

    const handleStatusFilterChange = (status: string) => {
        if (status === statusFilter) {
            setStatusFilter(null);
        } else {
            setStatusFilter(status);
        }

        setTimeout(() => refetch(), 0);
    };

    const handleCategoryFilterChange = (category: string) => {
        if (category === categoryFilter) {
            setCategoryFilter(null);
        } else {
            setCategoryFilter(category);
        }

        setTimeout(() => refetch(), 0);
    };

    const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

    const { isPending: isRiskRegisterPending, riskRegisterData, refetch } = useRiskRegister({
        projectId, searchKey, assignedToMe, quickFilters, status: statusFilter || undefined, category: categoryFilter || undefined
    });

    const [raiseRisk, setRaiseRisk] = useState<{
        open: boolean,
        editStatus: boolean,
        riskId: string | null
    }>({
        open: false,
        editStatus: false,
        riskId: null
    });

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const openDrawer = (riskId: string) => {
        const params = new URLSearchParams(searchParams);
        params.set("selected", "true");
        params.set("id", riskId);
        setSearchParams(params, { replace: true });
    };

    const closeDrawer = () => {
        const params = new URLSearchParams(searchParams);
        params.delete("selected");
        params.delete("id");
        setSearchParams(params, { replace: true });
    };

    if (isRiskRegisterPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box p={2}>


            <Box display={'flex'} alignItems={'center'} gap={2} mb={2}>

                <SearchField
                    label="Search Risks"
                    searchTerm={searchDraft}
                    handleSearch={handleChangeSearch}
                    handleClearSearch={handleClear}
                    handleApplySearch={handleSearch}
                />


                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                     <Tooltip
            title={
              hasActiveFilters ? (
                <Box sx={{ p: 0.5 }}>
                  {activeFilterLabels.map((label, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {label}
                    </Typography>
                  ))}
                </Box>
              ) : ""
            }
            placement="bottom-start"
            disableHoverListener={!hasActiveFilters}
            arrow
          >
            <Button
              startIcon={<FilterList />}
              variant={isModernTheme ? "outlined" : (hasActiveFilters ? "contained" : "text")}
              color="inherit"
              onClick={handleOpenFilter}
              sx={
                isModernTheme
                  ? {
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: "divider",
                    color: hasActiveFilters ? "primary.main" : "text.primary",
                    whiteSpace: "nowrap",
                  }
                  : hasActiveFilters
                  ? {
                    backgroundColor: "action.selected",
                    color: "text.primary",
                    "&:hover": { backgroundColor: "action.hover" },
                  }
                  : undefined
              }
            >
              {isModernTheme ? `Filters${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}` : `FILTERS${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}`}
            </Button>
          </Tooltip>
                    {
                        isAllowedToRaiseRisk && (
                            <Button startIcon={<AddIcon />} sx={{ ml: "auto" }} onClick={() => setRaiseRisk({ open: true, editStatus: false, riskId: null })}>
                                RAISE RISK
                            </Button>
                        )
                    }
                </Box>
            </Box>
            <Divider />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2,
                    justifyContent: 'flex-end',
                    mt: 2 
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="subtitle2">Raised by Me :</Typography>
                    <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 600 }}>Status :</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {getFilterOptions('RISK_OVERVIEW_STATUS').map((domain: any) => (
                            <Chip
                                key={domain.key}
                                label={domain.display_text}
                                color={domain.key === statusFilter ? 'primary' : 'default'}
                                variant={domain.key === statusFilter ? 'filled' : 'outlined'}
                                onClick={() => handleStatusFilterChange(domain.key)}
                            />
                        ))}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 600 }}>Category :</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {getFilterOptions('RISK_CATEGORY').map((domain: any) => (
                            <Chip
                                key={domain.key}
                                label={domain.display_text}
                                color={domain.key === categoryFilter ? 'primary' : 'default'}
                                variant={domain.key === categoryFilter ? 'filled' : 'outlined'}
                                onClick={() => handleCategoryFilterChange(domain.key)}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
         <Box display={'flex'} flexDirection={'column'} gap={2}>
                {
                    riskRegisterData.length === 0 ?
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            alignContent="center"
                            width="100%"
                            height="100%"
                            gap={2}
                            mt={5}
                        >
                            <Typography variant="body2" color="text.secondary">No risks found.</Typography>
                            <Typography variant="body2" color="text.secondary">Try adjusting your search or filter to find what you're looking for.</Typography>
                        </Box> :
                        riskRegisterData.map((value: RiskRegisterDataProps, key: number) => (
                            <Card sx={{ py: 1.5, px: 2 }} key={key} onClick={() => { openDrawer(value.risk_id) }}>
                                <Box display={'flex'} gap={3} alignItems={'center'}>
                                    <Avatar sx={{ bgcolor: value.status_display_text === 'Open' ? 'warning.main' : 'divider', borderRadius: 1 }}>
                                        <WarningIcon />
                                    </Avatar>
                                    <Box display={'flex'} flexDirection={'column'} gap={.8}>
                                        <Box display={'flex'} gap={2} alignItems={'center'}>
                                            <Typography variant="subtitle2">{value.title}</Typography>
                                            <StatusChip label={value.status_display_text} color={value.status_display_text === 'Open' ? 'amber' : 'default'} />
                                        </Box>
                                        <Box display={'flex'} gap={.5}>
                                            <Typography variant="caption" color="text.secondary">{value.project_name || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.project_ref || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.risk_ref || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.category_display_text || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(value?.review_date?.toString())}</Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="subtitle2" ml={'auto'}>{value.assessment_status}</Typography>
                                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                                    <StatusChip label={String(value.impact_display || 'N/A')} color={(value.impact === 'high' || value.impact === 'very_high') ? "red" : value.impact === 'medium' ? "amber" : (value.impact === 'low' || value.impact === 'very_low') ? "green" : "default"} variant="filled" />
                                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                                    <StatusChip label={String(value.probability_display || 'N/A')} color={(value.probability === 'high' || value.probability === 'very_high') ? "red" : value.probability === 'medium' ? "amber" : (value.probability === 'low' || value.probability === 'very_low') ? "green" : "default"} variant="filled" />
                                    <Typography variant="caption" color="text.secondary">Score</Typography>
                                    <ScroreCard score={value.score} />
                                </Box>
                            </Card>
                        ))
                }
            </Box>

            <Drawer
                isOpen={!!selected}
                header={
                    !!id &&
                    <RiskHeader
                        riskId={String(id)}
                        handleClose={() => closeDrawer()}
                    />
                }
            >
                <RiskDetails
                    riskId={String(id)}
                    onEdit={() => {
                        setRaiseRisk({ open: true, editStatus: true, riskId: String(id) });
                        closeDrawer();
                    }}
                    refetch={refetch}
                />
            </Drawer>

            <DrawerControl
                width="35%"
                title="Add/Edit Risk"
                subTitle={""}
                isOpen={raiseRisk.open}
                handleClose={() => { setRaiseRisk({ open: false, editStatus: false, riskId: null }) }}
                children={
                    <RaiseRisk
                        riskId={raiseRisk.riskId}
                        editStatus={raiseRisk.editStatus}
                        projectId={projectId}
                        onCancel={() => setRaiseRisk({ open: false, editStatus: false, riskId: null })}
                        refetch={refetch}
                    />
                }
            />
            <DrawerControl
                title="Filter Records"
                subTitle="Please apply following filters to refine results"
                isOpen={isOpenFilter}
                handleClose={handleCloseFilter}
            >
                <FilterSideBar hiddenFilters={["stakeholder"]} onClose={handleCloseFilter} />
            </DrawerControl>

        </Box>
    );
};